import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import bitmapFonts from 'src/lib/bitmapFonts';
import View from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import {
  ImageCoverView,
  ImageScaleView,
  ImageView,
} from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { Feature } from 'src/replicant/analytics';
import TierView from './TierView';
import { assertNever } from 'src/replicant/utils';
import { getDecorationPath } from '../decoration/helpers';
import StateObserver from 'src/StateObserver';
import { parseAmount } from 'src/lib/utils';
import * as Inventory from 'src/replicant/inventory';
import { CatalogItem } from 'src/replicant/catalog/catalog.ruleset';
import { animateCoins } from '../animation/CurrencyAnimation';
import { getAccessoryPath } from '../accessories/helpers';

import { AssetGroup } from '@play-co/timestep-core';

// Return true if user ok button
// Return false is user pressed cancel button
type CloseHandler = (result: boolean) => void;
type SupportedCurrency = 'b_points'; // bPoints

/**
 * Catalog sell decoration or accessory item,
 * It doesn't contain logic it just show item and return user choice.
 */
export default class PopupCatalogItemSell extends PopupBasic {
  private buttonBack: ButtonScaleViewWithText;
  private buttonOk: ButtonScaleViewWithText;
  private close: CloseHandler;
  private image: ImageCoverView;
  private tier: TierView;
  private itemName!: LangBitmapFontTextView;
  private itemRoom!: LangBitmapFontTextView;
  private itemTheme!: LangBitmapFontTextView;
  private setFullPrice: (currency: SupportedCurrency, price: number) => void;
  private setBalanceAfter: (currency: SupportedCurrency, price: number) => void;
  private item!: CatalogItem;
  private bottomLayout: View;

  /**
   * Constructor
   */
  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      superview: opts.superview,
      close: () => opts.close(false),
      zIndex: 10000,
      width: 563,
      height: 784,
      darkerBg: true,
      skipMessage: true,
      tracking: {
        feature: Feature.CATALOG._,
        $subFeature: Feature.CATALOG.POPUP_ITEM_SELL._,
      },
    });

    this.close = opts.close;
    this.buttonClose.removeFromSuperview();

    this.buttonBack = new ButtonScaleViewWithText({
      ...uiConfig.buttons.cancel,
      superview: this.box,
      labelOffsetY: 0,
      labelOffsetX: -9,
      fontSize: 40,
      font: bitmapFonts('Body'),
      x: 144,
      y: 705,
      width: 228,
      height: 94.7,
      centerOnOrigin: true,
      localeText: () => i18n('catalog.popupItemSell.back'),
      onClick: async () => {
        sounds.sfx.play('cancel');
        return opts.close(false);
      },
      tracking: {
        feature: Feature.CATALOG._,
        $subFeature: Feature.CATALOG.POPUP_ITEM_SELL.BACK,
      },
    });

    this.buttonOk = new ButtonScaleViewWithText({
      ...uiConfig.buttons.secondary,
      superview: this.box,
      labelOffsetY: 0,
      labelOffsetX: -9,
      fontSize: 40,
      font: bitmapFonts('Body'),
      x: 418,
      y: 705,
      width: 228,
      height: 94.7,
      centerOnOrigin: true,
      localeText: () => i18n('catalog.popupItemSell.sell'),
      onClick: async () => {
        sounds.sfx.play('ok');
        await this.animateCoins(this.item.sellPrice);
        opts.close(true);
      },
      tracking: {
        feature: Feature.CATALOG._,
        $subFeature: Feature.CATALOG.POPUP_ITEM_SELL.OK,
      },
    });

    this.createTitle(this.box);
    this.createDivider(this.box, { y: 126 });

    this.image = this.createItemImage(this.box);

    this.tier = new TierView({
      superview: this.box,
      x: 62,
      y: 392,
      width: 187,
      height: 38,
      star: {
        width: 45,
        height: 46,
      },
    });

    this.itemName = this.createItemName(this.box);

    this.createDivider(this.box, { y: 485 });

    this.bottomLayout = new View({
      superview: this.box,
      x: 0,
      y: 485,
      width: this.box.style.width,
      height: 160,
    });

    // Labels
    this.setFullPrice = this.createLabel(this.bottomLayout, {
      text: i18n('catalog.popupItemSell.sellingPrice'),
      y: 27,
      width: 481,
    });

    this.setBalanceAfter = this.createLabel(this.bottomLayout, {
      text: i18n('catalog.popupItemSell.balanceAfter'),
      y: 90,
      width: 481,
    });
  }

  protected getAssetsGroup() {
    return new AssetGroup([
      'assets/ui/catalog/icons/itemdetail_room.png',
      'assets/ui/catalog/icons/itemdetail_theme.png',
      'assets/ui/decoration/frames/rectangle_light.png',
      'assets/ui/hud/icons/hud_coin.png',
    ]);
  }

  /**
   * On dialog init
   */
  public init(item: CatalogItem) {
    super.init({
      title: i18n('catalog.popupItemSell.title'),
    });

    this.item = item;

    const lang = StateObserver.getLocale();

    this.tier.setTier(item.tier, item.maxTier);

    if (item.canSell) {
      const coinsBalanceAfter = this.balanceAfter(
        item.sellCurrency,
        item.sellPrice,
      );
      this.setFullPrice(item.sellCurrency, item.sellPrice);
      this.setBalanceAfter(item.sellCurrency, coinsBalanceAfter);
    }

    const disableButton = !item.canSell && !item.canFuse;
    this.buttonOk.setDisabled(disableButton);

    switch (lang) {
      case 'en':
        this.itemName.localeText = () => item.name_en;
        break;
      case 'ja':
        this.itemName.localeText = () => item.name_jp;
        break;
      default:
        assertNever(lang);
    }

    const type = item.type;

    switch (type) {
      case 'decoration':
        this.image.setImage(getDecorationPath(item.image));
        this.itemRoom = this.createItemRoom(this.box, () =>
          i18n(`decoration.rooms.${item.room}`),
        );
        this.itemTheme = this.createItemTheme(this.box, () =>
          i18n(`decoration.themes.${item.theme}`),
        );

        break;
      case 'accessory':
        this.image.setImage(getAccessoryPath(item.image, item.tierId));
        this.itemTheme = this.createItemTheme(this.box, () =>
          i18n(`accessory.themes.${item.theme}`),
        );
        break;
      default:
        assertNever(type);
    }
  }

  /**
   * Create title
   */
  private createTitle(superview: View) {
    return new LangBitmapFontTextView({
      ...uiConfig.popups.title,
      superview,
      x: superview.style.width / 2,
      y: 94,
      size: 27,
      width: 531,
      height: 35,
      font: bitmapFonts('Body'),
      localeText: () => i18n('catalog.popupItemSell.message'),
      letterSpacing: -2,
      centerOnOrigin: true,
      color: '#BF8147',
    });
  }

  /**
   * Create divider
   */
  private createDivider(superview: View, position: { y: number }) {
    return new View({
      superview,
      ...position,
      x: superview.style.width / 2,
      width: 487,
      height: 5,
      backgroundColor: '#FDC381',
      centerOnOrigin: true,
    });
  }

  /**
   * Create item image
   */
  private createItemImage(superview: View) {
    return new ImageCoverView({
      superview,
      mode: 'contain',
      verticalAlign: 'center',
      horizontalAlign: 'center',
      x: 50,
      y: 190,
      width: 208,
      height: 188,
    });
  }

  /**
   * Create item name
   */
  private createItemName(superview: View) {
    const name = new LangBitmapFontTextView({
      ...uiConfig.popups.title,
      superview,
      x: 272,
      y: 214,
      size: 32,
      width: 260,
      height: 38,
      align: 'left',
      font: bitmapFonts('Body'),
      localeText: () => 'Unknown',
      letterSpacing: -2,
      color: '#BF8147',
    });

    new View({
      superview: name,
      x: 6,
      y: 44,
      width: 56,
      height: 2,
      backgroundColor: '#BF8147',
    });

    return name;
  }

  /**
   * Create room name label
   */
  private createItemRoom(superview: View, localeText: () => string) {
    const view = new View({
      superview,
      x: 277,
      y: 286,
      width: 260,
      height: 26,
    });

    const name = new LangBitmapFontTextView({
      ...uiConfig.popups.title,
      superview: view,
      x: 37,
      y: 0,
      size: 24,
      width: 222,
      height: 26,
      align: 'left',
      font: bitmapFonts('Body'),
      localeText,
      letterSpacing: -2,
      color: '#9A7171',
    });

    new ImageView({
      superview: view,
      image: 'assets/ui/catalog/icons/itemdetail_room.png',
      x: 0,
      y: 0,
      width: 26,
      height: 25,
    });

    return name;
  }

  /**
   * Create room name label
   */
  private createItemTheme(superview: View, localeText: () => string) {
    const view = new View({
      superview,
      x: 277,
      y: this.itemRoom ? 326 : 286,
      width: 260,
      height: 26,
    });

    const name = new LangBitmapFontTextView({
      ...uiConfig.popups.title,
      superview: view,
      x: 37,
      y: 0,
      size: 24,
      width: 222,
      height: 26,
      align: 'left',
      font: bitmapFonts('Body'),
      localeText,
      letterSpacing: -2,
      color: '#9A7171',
    });

    new ImageView({
      superview: view,
      image: 'assets/ui/catalog/icons/itemdetail_theme.png',
      x: 0,
      y: 0,
      width: 26,
      height: 26,
    });

    return name;
  }

  /**
   * Create label for prices
   */
  private createLabel(
    superview: View,
    opts: { text: string; y: number; width: number },
  ) {
    const frame = new ImageScaleView({
      tag: 'frame',
      superview,
      x: (superview.style.width - 481) * 0.5,
      y: opts.y,
      width: opts.width,
      height: 47,
      image: 'assets/ui/decoration/frames/rectangle_light.png',
      scaleMethod: '3slice',
      sourceSlices: {
        horizontal: { left: 43, right: 43 },
      },
      visible: false,
    });

    new LangBitmapFontTextView({
      tag: 'label',
      superview: frame,
      x: 20,
      y: 0,
      width: 200,
      height: 47,
      align: 'left',
      verticalAlign: 'center',
      size: 24,
      color: '#BF8147',
      localeText: () => opts.text,
      font: bitmapFonts('Body'),
    });

    const priceView = new LangBitmapFontTextView({
      tag: 'price',
      superview: frame,
      x: opts.width - 260,
      y: 0,
      width: 200,
      height: 47,
      align: 'right',
      verticalAlign: 'center',
      size: 24,
      color: '#9A7171',
      localeText: () => '0',
      font: bitmapFonts('Body'),
    });

    const currencyIcon = new ImageView({
      tag: 'icon',
      superview: frame,
      width: 54,
      height: 54,
      x: opts.width - 48,
      y: 0,
    });

    return (currency: SupportedCurrency, price: number) => {
      frame.show();
      priceView.color = price >= 0 ? '#9A7171' : '#cf0000';
      priceView.localeText = () => parseAmount(price);
      currencyIcon.setImage(this.getCurrencyIcon(currency));
    };
  }

  /**
   * Animate coins
   */
  private async animateCoins(coins: number) {
    // Start position
    const iconPos = this.bottomLayout.getPosition(this.root);
    const start = {
      x: iconPos.x + 500,
      y: iconPos.y + 40,
    };

    // Start coin animation
    await animateCoins({
      superview: this.root,
      start: { position: start, scale: 1 },
      count: Math.min(coins, 1000),
    });
  }

  /**
   * Return currency icon
   */
  private getCurrencyIcon(currency: SupportedCurrency) {
    switch (currency) {
      case 'b_points':
        return 'assets/ui/hud/icons/hud_coin.png';
      default:
        assertNever(currency);
    }
  }

  /**
   * Balance after
   */
  private balanceAfter(currency: SupportedCurrency, fullPrice: number): number {
    const { user } = StateObserver.getState();

    switch (currency) {
      case 'b_points':
        return Inventory.getters.getPoints(user) + fullPrice;
      default:
        assertNever(currency);
    }
  }
}
